import React, { useState } from 'react';
import { useTheme } from '../ThemeContext';

const ThemeChooser = ({ onComplete }) => {
  const { toggleTheme } = useTheme();
  const [hovering, setHovering] = useState(null);

  const choose = (picked) => {
    const current = localStorage.getItem('ai_covo_theme');
    if (picked === 'matrix' && current !== 'matrix') toggleTheme();
    if (picked === 'istituzionale' && current !== 'istituzionale') toggleTheme();
    localStorage.setItem('ai_covo_theme', picked);
    onComplete();
  };

  return (
    <div data-testid="theme-chooser" className="fixed inset-0 z-[99] bg-black flex items-center justify-center overflow-y-auto py-8 px-4"
      style={{ animation: 'chooserFadeIn 0.4s ease-out' }}>
      <style>{`
        @keyframes chooserFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes chooserSlideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      <div className="max-w-3xl w-full" style={{ animation: 'chooserSlideUp 0.4s ease-out 0.1s both' }}>

        {/* TITOLO CHIARO */}
        <div className="text-center mb-10">
          <p className="font-mono font-black text-[#00FF41] text-2xl mb-4" style={{ textShadow: '0 0 15px rgba(0,255,65,0.3)' }}>&lt;/AI_covo.tech&gt;®</p>
          <h1 className="text-3xl md:text-4xl font-black text-white mb-3">
            Scegli il Tema di Navigazione
          </h1>
          <p className="text-white text-lg font-bold">
            Che tipo sei? Scegli come vuoi esplorare il sito.
          </p>
        </div>

        {/* DUE COLONNE — vertical su mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">

          {/* ═══ DARK MODE ═══ */}
          <button
            onClick={() => choose('matrix')}
            onMouseEnter={() => setHovering('dark')}
            onMouseLeave={() => setHovering(null)}
            data-testid="choose-dark"
            className={`text-left rounded-2xl border-2 overflow-hidden transition-all duration-300 ${hovering === 'dark' ? 'scale-[1.02] border-[#00FF41]' : 'border-[#00FF41]/50'}`}
            style={{ background: '#0A0A0A' }}
          >
            {/* Preview */}
            <div className="h-28 md:h-36 bg-[#050505] flex items-center justify-center relative">
              <div className="text-center">
                <p className="font-mono font-black text-[#00FF41] text-xl" style={{ textShadow: '0 0 10px rgba(0,255,65,0.4)' }}>&lt;/AI_covo.tech&gt;</p>
                <p className="text-white text-[10px] font-mono font-bold tracking-widest mt-1">DIGITAL HUB SOCIALE</p>
              </div>
              <div className="absolute top-3 right-3 bg-[#00FF41] text-black font-black text-[10px] px-3 py-1 rounded-full tracking-wider">
                CONSIGLIATO
              </div>
            </div>

            {/* Info */}
            <div className="p-5">
              <h3 className="text-xl font-black text-white mb-2">Dark Mode</h3>
              <p className="text-white font-bold text-sm mb-4">
                Sfondo nero, scritte luminose. Perfetto per tutti.
              </p>

              <div className="space-y-2 mb-5">
                <div className="flex items-start gap-2 text-xs">
                  <span className="text-[#00FF41] font-bold mt-0.5">+</span>
                  <span className="text-white"><strong className="text-[#00FF41]">Risparmia batteria</strong> — fino al 63% in meno su schermi OLED (Google, 2018)</span>
                </div>
                <div className="flex items-start gap-2 text-xs">
                  <span className="text-[#00FF41] font-bold mt-0.5">+</span>
                  <span className="text-white"><strong className="text-[#00FF41]">Protegge gli occhi</strong> — meno luce blu, meno affaticamento (Nature, 2020)</span>
                </div>
                <div className="flex items-start gap-2 text-xs">
                  <span className="text-[#00FF41] font-bold mt-0.5">+</span>
                  <span className="text-white"><strong className="text-[#00FF41]">Migliora il sonno</strong> — la melatonina non viene soppressa (J. Sleep Research)</span>
                </div>
              </div>

              {/* CTA grande */}
              <div className="w-full py-3 rounded-xl text-center font-black text-sm uppercase tracking-wider transition-all"
                style={{ background: '#00FF41', color: '#000' }}>
                CONTINUA CON DARK MODE
              </div>
            </div>
          </button>

          {/* ═══ CHIARO ISTITUZIONALE ═══ */}
          <button
            onClick={() => choose('istituzionale')}
            onMouseEnter={() => setHovering('light')}
            onMouseLeave={() => setHovering(null)}
            data-testid="choose-light"
            className={`text-left rounded-2xl border-2 overflow-hidden transition-all duration-300 ${hovering === 'light' ? 'scale-[1.02] border-[#C4532D]' : 'border-zinc-600'}`}
            style={{ background: '#FAF9F6' }}
          >
            {/* Preview */}
            <div className="h-28 md:h-36 bg-[#FAF9F6] flex items-center justify-center">
              <div className="text-center">
                <p className="font-mono font-black text-[#C4532D] text-xl">&lt;/AI_covo.tech&gt;</p>
                <p className="text-[#5A6B80] text-[10px] font-mono font-bold tracking-widest mt-1">DIGITAL HUB SOCIALE</p>
              </div>
            </div>

            {/* Info */}
            <div className="p-5">
              <h3 className="text-xl font-black text-[#1B2A4A] mb-2">Chiaro Istituzionale</h3>
              <p className="text-[#1B2A4A] font-bold text-sm mb-4">
                Sfondo chiaro, leggibile, familiare. Come un sito del Comune.
              </p>

              <div className="space-y-2 mb-5">
                <div className="flex items-start gap-2 text-xs">
                  <span className="text-[#C4532D] font-bold mt-0.5">+</span>
                  <span className="text-[#1B2A4A]"><strong className="text-[#C4532D]">Massima leggibilità</strong> — ideale per chi ha difficoltà visive</span>
                </div>
                <div className="flex items-start gap-2 text-xs">
                  <span className="text-[#C4532D] font-bold mt-0.5">+</span>
                  <span className="text-[#1B2A4A]"><strong className="text-[#C4532D]">Familiare</strong> — come un sito comunale o ministeriale</span>
                </div>
                <div className="flex items-start gap-2 text-xs">
                  <span className="text-[#C4532D] font-bold mt-0.5">+</span>
                  <span className="text-[#1B2A4A]"><strong className="text-[#C4532D]">Alto contrasto</strong> — testo scuro su sfondo chiaro, facile da leggere</span>
                </div>
              </div>

              {/* CTA grande */}
              <div className="w-full py-3 rounded-xl text-center font-black text-sm uppercase tracking-wider transition-all"
                style={{ background: '#C4532D', color: '#fff' }}>
                CONTINUA CON TEMA CHIARO
              </div>
            </div>
          </button>
        </div>

        {/* Nota finale */}
        <p className="text-center text-white text-sm font-bold">
          Puoi cambiare tema in qualsiasi momento dal cerchio in alto a destra.
        </p>
      </div>
    </div>
  );
};

export default ThemeChooser;
